import { cellToBox, boxKey, cellKey, BOX_SIZE } from '../core/coordinates';
import { isMine, DEFAULT_MINE_PROBABILITY, registerSafeOpening } from '../core/mineGenerator';
import { CellState } from '../core/cell';
import { BoxStatus, isBoxCompleted } from '../core/box';
import { getAdjacentMineCount } from '../mechanics/adjacent';
import { performFloodFill } from '../mechanics/floodFill';
import { getUnlockCost } from '../mechanics/penalty';

const OFFSETS = [
  [-1, -1], [0, -1], [1, -1],
  [-1,  0],          [1,  0],
  [-1,  1], [0,  1], [1,  1],
];

export interface GameSnapshot {
  seed: string;
  rewardPoints: number;
  failureCount: number;
  cameraX: number;
  cameraY: number;
  zoom: number;
  boxes: Map<string, BoxStatus>;
  cells: Map<string, CellState>;
}

export interface RevealResult {
  hitMine: boolean;
  bustedBox?: { boxX: number; boxY: number };
  revealedCells: { x: number; y: number }[];
  completedBoxes: { boxX: number; boxY: number }[];
  rejectedUnexplored?: boolean;
}

export interface FlagResult {
  toggled: boolean;
  newState?: CellState;
  completedBoxes: { boxX: number; boxY: number }[];
  rejectedUnexplored?: boolean;
}

export interface UnlockResult {
  success: boolean;
  reason?: string;
  cost?: number;
  newRewardPoints?: number;
  newFailureCount?: number;
}

export class GameEngine {
  public seed: string;
  public rewardPoints: number;
  public failureCount: number;
  public cameraX: number;
  public cameraY: number;
  public zoom: number;

  // Key format: "boxX,boxY" -> status
  public boxes: Map<string, BoxStatus> = new Map();
  // Key format: "x,y" -> state
  public cells: Map<string, CellState> = new Map();

  public mineProbability: number = DEFAULT_MINE_PROBABILITY;

  constructor(
    seed: string = Math.random().toString(36).substring(2, 10),
    rewardPoints: number = 0,
    failureCount: number = 0,
    cameraX: number = 0,
    cameraY: number = 0,
    zoom: number = 1
  ) {
    this.seed = seed;
    this.rewardPoints = rewardPoints;
    this.failureCount = failureCount;
    this.cameraX = cameraX;
    this.cameraY = cameraY;
    this.zoom = zoom;

    // Register initial origin (0,0) as safe opening center
    registerSafeOpening(0, 0);
  }

  public getBoxStatus(boxX: number, boxY: number): BoxStatus {
    const key = boxKey(boxX, boxY);
    return this.boxes.get(key) || 'ACTIVE';
  }

  public setBoxStatus(boxX: number, boxY: number, status: BoxStatus): void {
    const key = boxKey(boxX, boxY);
    this.boxes.set(key, status);
  }

  public getCellState(x: number, y: number): CellState {
    const key = cellKey(x, y);
    return this.cells.get(key) || 'HIDDEN';
  }

  public setCellState(x: number, y: number, state: CellState): void {
    const key = cellKey(x, y);
    if (state === 'HIDDEN') {
      this.cells.delete(key);
    } else {
      this.cells.set(key, state);
    }
  }

  /**
   * Checks if a cell (x, y) is directly adjacent (in 8-neighborhood) to at least one REVEALED tile.
   * If 0 cells are revealed on the board yet, allows the initial start tap.
   */
  public isCellAdjacentToRevealed(x: number, y: number): boolean {
    let totalRevealed = 0;
    for (const state of this.cells.values()) {
      if (state === 'REVEALED') {
        totalRevealed++;
        break;
      }
    }

    // Allow initial game start tap if no cells are revealed anywhere yet
    if (totalRevealed === 0) return true;

    // Must be adjacent (in 8-neighborhood) to at least one REVEALED cell
    for (const [dx, dy] of OFFSETS) {
      if (this.getCellState(x + dx, y + dy) === 'REVEALED') {
        return true;
      }
    }

    return false;
  }

  /**
   * Helper to check if a 10x10 box has any revealed cells.
   */
  private hasRevealedCellsInBox(boxX: number, boxY: number): boolean {
    const startX = boxX * BOX_SIZE;
    const startY = boxY * BOX_SIZE;
    for (let cy = 0; cy < BOX_SIZE; cy++) {
      for (let cx = 0; cx < BOX_SIZE; cx++) {
        if (this.getCellState(startX + cx, startY + cy) === 'REVEALED') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Primary action: Reveal cell at (x, y).
   * Enforces:
   * 1. Cell MUST be adjacent to an already REVEALED tile (revealed frontier).
   * 2. First-click safe 0-mine cascade opening.
   * 3. Chording mechanic on revealed numbers.
   */
  public revealCell(x: number, y: number): RevealResult {
    const { boxX, boxY } = cellToBox(x, y);
    const boxStatus = this.getBoxStatus(boxX, boxY);

    if (boxStatus === 'BUSTED' || boxStatus === 'COMPLETED') {
      return { hitMine: false, revealedCells: [], completedBoxes: [] };
    }

    const currentState = this.getCellState(x, y);
    if (currentState === 'FLAGGED') {
      return { hitMine: false, revealedCells: [], completedBoxes: [] };
    }

    // CHORDING MECHANIC: Tap on an ALREADY REVEALED cell
    if (currentState === 'REVEALED') {
      const adjCount = getAdjacentMineCount(this.seed, x, y, this.mineProbability);
      if (adjCount > 0) {
        let flagCount = 0;
        const hiddenNeighbors: { x: number; y: number }[] = [];

        for (const [dx, dy] of OFFSETS) {
          const nx = x + dx;
          const ny = y + dy;
          const st = this.getCellState(nx, ny);
          if (st === 'FLAGGED') {
            flagCount++;
          } else if (st === 'HIDDEN') {
            hiddenNeighbors.push({ x: nx, y: ny });
          }
        }

        if (flagCount === adjCount && hiddenNeighbors.length > 0) {
          let hitMine = false;
          let bustedBoxData: { boxX: number; boxY: number } | undefined = undefined;
          const allRevealed: { x: number; y: number }[] = [];
          const completedBoxes: { boxX: number; boxY: number }[] = [];

          for (const n of hiddenNeighbors) {
            const chordResult = this.revealCell(n.x, n.y);
            if (chordResult.hitMine) {
              hitMine = true;
              bustedBoxData = chordResult.bustedBox;
            } else {
              allRevealed.push(...chordResult.revealedCells);
              for (const cb of chordResult.completedBoxes) {
                if (!completedBoxes.some((b) => b.boxX === cb.boxX && b.boxY === cb.boxY)) {
                  completedBoxes.push(cb);
                }
              }
            }
          }

          return {
            hitMine,
            bustedBox: bustedBoxData,
            revealedCells: allRevealed,
            completedBoxes,
          };
        }
      }
      return { hitMine: false, revealedCells: [], completedBoxes: [] };
    }

    // ENFORCE FRONTIER EXPLORATION RULE:
    // Only allow revealing hidden cells directly adjacent to an already REVEALED tile!
    if (!this.isCellAdjacentToRevealed(x, y)) {
      return { hitMine: false, revealedCells: [], completedBoxes: [], rejectedUnexplored: true };
    }

    // FIRST-CLICK BOX OPENING GUARANTEE:
    if (!this.hasRevealedCellsInBox(boxX, boxY)) {
      registerSafeOpening(x, y);
    }

    // Check if cell is a mine
    if (isMine(this.seed, x, y, this.mineProbability)) {
      this.setBoxStatus(boxX, boxY, 'BUSTED');
      return {
        hitMine: true,
        bustedBox: { boxX, boxY },
        revealedCells: [],
        completedBoxes: [],
      };
    }

    // Cell is safe
    const newlyRevealed: { x: number; y: number }[] = [];
    const adjCount = getAdjacentMineCount(this.seed, x, y, this.mineProbability);

    if (adjCount === 0) {
      const flood = performFloodFill({
        seed: this.seed,
        startX: x,
        startY: y,
        getCellState: (cx, cy) => this.getCellState(cx, cy),
        getBoxStatus: (bx, by) => this.getBoxStatus(bx, by),
        mineProbability: this.mineProbability,
      });

      for (const cell of flood) {
        this.setCellState(cell.x, cell.y, 'REVEALED');
        newlyRevealed.push(cell);
      }
    } else {
      this.setCellState(x, y, 'REVEALED');
      newlyRevealed.push({ x, y });
    }

    // Check for box completions in affected boxes
    const affectedBoxes = new Set<string>();
    for (const cell of newlyRevealed) {
      const b = cellToBox(cell.x, cell.y);
      affectedBoxes.add(boxKey(b.boxX, b.boxY));
    }

    const completedBoxes: { boxX: number; boxY: number }[] = [];
    for (const key of affectedBoxes) {
      const [bx, by] = key.split(',').map(Number);
      if (this.getBoxStatus(bx, by) === 'ACTIVE') {
        const completed = isBoxCompleted(
          this.seed,
          bx,
          by,
          (cx, cy) => this.getCellState(cx, cy),
          this.mineProbability
        );

        if (completed) {
          this.finalizeBoxCompletion(bx, by);
          completedBoxes.push({ boxX: bx, boxY: by });
        }
      }
    }

    return {
      hitMine: false,
      revealedCells: newlyRevealed,
      completedBoxes,
    };
  }

  /**
   * Toggle flag state at (x, y)
   */
  public toggleFlag(x: number, y: number): FlagResult {
    const { boxX, boxY } = cellToBox(x, y);
    const boxStatus = this.getBoxStatus(boxX, boxY);

    if (boxStatus === 'BUSTED' || boxStatus === 'COMPLETED') {
      return { toggled: false, completedBoxes: [] };
    }

    const currentState = this.getCellState(x, y);
    if (currentState === 'REVEALED') {
      return { toggled: false, completedBoxes: [] };
    }

    // Enforce adjacent revealed tile rule for flagging as well
    if (currentState === 'HIDDEN' && !this.isCellAdjacentToRevealed(x, y)) {
      return { toggled: false, completedBoxes: [], rejectedUnexplored: true };
    }

    const newState: CellState = currentState === 'FLAGGED' ? 'HIDDEN' : 'FLAGGED';
    this.setCellState(x, y, newState);

    const completedBoxes: { boxX: number; boxY: number }[] = [];
    if (this.getBoxStatus(boxX, boxY) === 'ACTIVE') {
      const completed = isBoxCompleted(
        this.seed,
        boxX,
        boxY,
        (cx, cy) => this.getCellState(cx, cy),
        this.mineProbability
      );

      if (completed) {
        this.finalizeBoxCompletion(boxX, boxY);
        completedBoxes.push({ boxX, boxY });
      }
    }

    return {
      toggled: true,
      newState,
      completedBoxes,
    };
  }

  /**
   * Unlocks a busted box if player has sufficient reward points
   */
  public unlockBustedBox(boxX: number, boxY: number): UnlockResult {
    const currentStatus = this.getBoxStatus(boxX, boxY);
    if (currentStatus !== 'BUSTED') {
      return { success: false, reason: 'box_not_busted' };
    }

    const cost = getUnlockCost(this.failureCount);
    if (this.rewardPoints < cost) {
      return { success: false, reason: 'insufficient_points', cost };
    }

    // Deduct points, advance penalty index
    this.rewardPoints -= cost;
    this.failureCount += 1;

    // Reset box to ACTIVE
    this.setBoxStatus(boxX, boxY, 'ACTIVE');

    // Clear all revealed and flagged cells inside this 10x10 box
    const startX = boxX * BOX_SIZE;
    const startY = boxY * BOX_SIZE;
    for (let cy = 0; cy < BOX_SIZE; cy++) {
      for (let cx = 0; cx < BOX_SIZE; cx++) {
        this.setCellState(startX + cx, startY + cy, 'HIDDEN');
      }
    }

    // Register safe opening for next attempt in this box
    registerSafeOpening(startX + 5, startY + 5);

    return {
      success: true,
      cost,
      newRewardPoints: this.rewardPoints,
      newFailureCount: this.failureCount,
    };
  }

  /**
   * Finalizes a completed box:
   * 1. Auto-reveals ALL non-mine safe cells.
   * 2. Auto-places flags 🚩 on ALL mine cells.
   * 3. Marks box COMPLETED permanently.
   * 4. Awards 1 Reward Point.
   */
  private finalizeBoxCompletion(boxX: number, boxY: number): void {
    const startX = boxX * BOX_SIZE;
    const startY = boxY * BOX_SIZE;

    for (let cy = 0; cy < BOX_SIZE; cy++) {
      for (let cx = 0; cx < BOX_SIZE; cx++) {
        const x = startX + cx;
        const y = startY + cy;
        if (isMine(this.seed, x, y, this.mineProbability)) {
          this.setCellState(x, y, 'FLAGGED');
        } else {
          this.setCellState(x, y, 'REVEALED');
        }
      }
    }

    this.setBoxStatus(boxX, boxY, 'COMPLETED');
    this.rewardPoints += 1;
  }

  /**
   * Reset game to a fresh seed & state
   */
  public resetGame(newSeed?: string): void {
    this.seed = newSeed || Math.random().toString(36).substring(2, 10);
    this.rewardPoints = 0;
    this.failureCount = 0;
    this.cameraX = 0;
    this.cameraY = 0;
    this.zoom = 1;
    this.boxes.clear();
    this.cells.clear();
    registerSafeOpening(0, 0);
  }
}
